import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, from, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { Auth, User, getIdToken as firebaseGetIdToken, signOut as firebaseSignOut } from '@angular/fire/auth'; // Updated imports
import { Router } from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // Exclusion list: Add URLs or URL patterns that should NOT be intercepted
  private excludedUrls: string[] = [
    'fcm.googleapis.com/fcm/send', // Example: FCM API (matches anywhere in URL)
    '/assets/' // Example: Static assets
    // Consider if 'securetoken.googleapis.com' (Firebase token refresh) should be excluded,
    // though typically it's not an issue as it's a different flow.
  ];

  constructor(private auth: Auth, private router: Router) { } // Use modular Auth

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if the request should be excluded
    if (this.shouldExclude(request)) {
      return next.handle(request); // Bypass interception
    }

    // Add Authorization header to the request
    const authenticatedRequest = this.addAuthorizationHeader(request);

    return next.handle(authenticatedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Pass the original request (before adding potentially stale token)
          // to ensure it's retried correctly with a fresh token.
          return this.handle401Error(request, next);
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private shouldExclude(request: HttpRequest<any>): boolean {
    const url = request.url.toLowerCase();
    return this.excludedUrls.some(excludedUrl => url.includes(excludedUrl.toLowerCase()));
  }

  private addAuthorizationHeader(request: HttpRequest<any>): HttpRequest<any> {
    // This token is set by AuthService on login/auth state change and stored in sessionStorage
    const idToken = sessionStorage.getItem('idToken');
    if (idToken) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${idToken}`
        }
      });
    }
    return request;
  }

  private handle401Error(originalRequest: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const user: User | null = this.auth.currentUser;

      if (user) {
        return from(firebaseGetIdToken(user, true)).pipe( // Force refresh token
          switchMap((newIdToken: string) => {
            sessionStorage.setItem('idToken', newIdToken); // Update sessionStorage
            this.refreshTokenSubject.next(newIdToken);
            // Retry the original request with the new token
            return next.handle(this.addAuthorizationHeader(originalRequest.clone()));
          }),
          catchError((refreshError) => {
            console.error('Error refreshing token:', refreshError);
            return this.logoutAndNavigate(refreshError);
          }),
          finalize(() => {
            this.isRefreshing = false;
          })
        );
      } else {
        // No user, cannot refresh.
        return this.logoutAndNavigate(new Error('No user session to refresh token.'));
      }
    } else {
      // If already refreshing, wait for the new token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(() => {
          // Retry the original request with the new token
          return next.handle(this.addAuthorizationHeader(originalRequest.clone()));
        })
      );
    }
  }

  private logoutAndNavigate(error: any): Observable<never> {
    // Perform sign-out and navigation, then re-throw the error.
    return from(firebaseSignOut(this.auth)).pipe(
      switchMap(() => {
        sessionStorage.removeItem('idToken'); // Be specific if other session items are used
        // sessionStorage.clear(); // Or clear all if appropriate
        this.router.navigate(['/login']);
        return throwError(() => error); // Re-throw the error that led to logout
      }),
      catchError(signOutError => {
        // Even if signout fails, navigate and clear session
        console.error('Error during sign out after token refresh failure:', signOutError);
        sessionStorage.removeItem('idToken');
        // sessionStorage.clear();
        this.router.navigate(['/login']);
        return throwError(() => error); // Re-throw the original error
      })
    );
  }
}
